/**
 * The Email settings page for a person without an account (S3-20). Opening it changes nothing; only
 * Save does. It loads in the browser, so a link scanner gets the shell and asks nothing of the server.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { get, postJson } from '../../api/httpClient';
import { isBadLink, isRefused, tokenFrom } from '../../util/emailLinks';

export const readSettingsThunk = createAsyncThunk(
  'EmailSettingsPage/readSettings',
  (token, { rejectWithValue }) =>
    get('/api/email/settings', { token })
      .then(response => response.data.data)
      .catch(e => rejectWithValue(isBadLink(e) ? 'invalid' : 'failed'))
);

export const saveSettingsThunk = createAsyncThunk(
  'EmailSettingsPage/saveSettings',
  ({ token, on }, { dispatch, rejectWithValue }) =>
    postJson('/api/email/settings', { token, on })
      .then(() => on)
      .catch(async e => {
        // The address may have stopped since the page opened: show that instead of asking to retry.
        // Only the server's answer says so, since other refusals read the same.
        if (isRefused(e)) {
          const read = await dispatch(readSettingsThunk(token));
          return rejectWithValue(read.payload?.stopped ? 'stopped' : 'failed');
        }
        return rejectWithValue(isBadLink(e) ? 'invalid' : 'failed');
      })
);

const initialState = { status: 'loading', settings: null, saveStatus: 'idle' };

const emailSettingsPageSlice = createSlice({
  name: 'EmailSettingsPage',
  initialState,
  reducers: {
    linkMissing: state => {
      state.status = 'invalid';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(readSettingsThunk.pending, () => initialState)
      .addCase(readSettingsThunk.fulfilled, (state, action) => {
        state.status = 'ready';
        state.settings = action.payload;
      })
      .addCase(readSettingsThunk.rejected, (state, action) => {
        state.status = action.payload === 'invalid' ? 'invalid' : 'failed';
      })
      .addCase(saveSettingsThunk.pending, state => {
        state.saveStatus = 'saving';
      })
      .addCase(saveSettingsThunk.fulfilled, (state, action) => {
        state.saveStatus = 'saved';
        state.settings.on = action.payload;
      })
      .addCase(saveSettingsThunk.rejected, (state, action) => {
        if (action.payload === 'invalid') state.status = 'invalid';
        state.saveStatus = action.payload === 'stopped' ? 'idle' : 'failed';
      });
  },
});

export default emailSettingsPageSlice.reducer;

export const { linkMissing } = emailSettingsPageSlice.actions;

export const loadData = (params, search) => dispatch => {
  const token = tokenFrom(search);
  return token ? dispatch(readSettingsThunk(token)) : Promise.resolve(dispatch(linkMissing()));
};
